import { Status } from '../../../../common/components/Status/Status';
import {
  AccordionTitle,
  IdentityProviderActionButton,
  IdentityProvidersListWrapper,
} from './IdentityProvidersList.styles';
import { Box, Button, Flexbox, Icon, Spinner, Tooltip, Typography } from '@hiiretail/synergy-ui';
import { RichTableToolbar } from '../../../../common/components/RichTable/RichTableToolbar/RichTableToolbar';
import { useIntl } from 'react-intl';
import { AccessControl, Accordion, CommonMessages, Retry } from '../../../../common';
import { AccessDeniedButton } from '../../../../common/styles/AccessDenied.styles';
import { IdentityProvidersMessages } from '../../IdentityProviders.messages';
import { ErrorResponse } from '../../../../common/types/errorRespose';
import { OidcIdentityProviderDto, SamlIdentityProviderDto } from '../../../../api-client';
import React from 'react';
import { DisplayedField } from './utils/DisplayedFields';
import { useGetTenantAlias } from 'common/hooks/useGetTenantAlias/useGetTenantAlias';

type IdentityProvidersListProps = {
  identityProviders: OidcIdentityProviderDto[] | SamlIdentityProviderDto[] | null | undefined;
  primaryConnectionId?: string;
  displayedFields: DisplayedField[];
  refetchIdentityProviders: () => void;
  isLoading: boolean;
  onDisableButtonClick?: (id: { id: string }) => void;
  onEnableButtonClick?: (id: { id: string }) => void;
  onSetPrimaryButtonClick?: (id?: string) => void;
  onUnSetPrimaryButtonClick?: (id?: string) => void;
  onUpdateButtonClick?: (
    identityProviderDto: OidcIdentityProviderDto | SamlIdentityProviderDto,
  ) => void;
  isDisabling: boolean;
  isEnabling: boolean;
  isSetting: boolean;
  fetchError: ErrorResponse | null;
};

type IdentityProvidersListComponent = React.FC<IdentityProvidersListProps>;

export const IdentityProvidersList: IdentityProvidersListComponent = ({
  displayedFields,
  identityProviders,
  primaryConnectionId,
  refetchIdentityProviders,
  isLoading,
  onUpdateButtonClick,
  fetchError,
  onDisableButtonClick,
  onEnableButtonClick,
  onSetPrimaryButtonClick,
  onUnSetPrimaryButtonClick,
  isDisabling,
  isEnabling,
  isSetting,
}) => {
  const intl = useIntl();
  const tenantAlias = useGetTenantAlias();
  return (
    <>
      <RichTableToolbar
        permissions={{
          create: 'iam.provider.create',
          list: 'iam.provider.list',
        }}
        searchFieldLabel=""
        title="Browse Identity Providers"
        onSearchChange={() => void 0}
        hideSearch
      />
      <IdentityProvidersListWrapper container item direction="column" flex="1 1 auto" height="100%">
        {isLoading ? (
          <Spinner />
        ) : (
          <AccessControl
            renderIfNoAccess={
              <Flexbox
                container
                direction="column"
                item
                flex="1 1 auto"
                alignItems="center"
                justifyContent="center"
                gutter={2}
              >
                <Flexbox item>
                  <Icon.WarningTriangle size="lg" color={() => 'rgb(244, 67, 54)'} />
                </Flexbox>
                <Flexbox item>
                  <Typography variant="body2">
                    You don't have permissions to view identity providers
                  </Typography>
                </Flexbox>
                <Flexbox item>
                  <Typography variant="body2">
                    Required permissions:{' '}
                    <Typography variant="body2" weight="bold">
                      iam.provider.list
                    </Typography>
                  </Typography>
                </Flexbox>
              </Flexbox>
            }
            permission="iam.provider.list"
          >
            {fetchError && (
              <Retry
                text="identity providers"
                handleClick={refetchIdentityProviders}
                size="sm"
                justifyContent="center"
              />
            )}
            <>
              {!fetchError && identityProviders && (
                <>
                  {identityProviders.length === 0 && (
                    <Flexbox container alignItems="center" justifyContent="center" height="100%">
                      <Typography variant="body2">No Identity Providers found</Typography>
                    </Flexbox>
                  )}
                  {!!identityProviders.length && (
                    <Box py={4} px={8}>
                      <Flexbox container direction="column" gutter={4}>
                        {identityProviders.map((provider, index) => (
                          <Flexbox item key={provider.id}>
                            <Accordion
                              title={
                                <AccordionTitle variant="header3">{provider.name}</AccordionTitle>
                              }
                              subTitle={
                                <Flexbox container>
                                  <Status status={provider.status} variant="body2" />
                                  <Box mr={2} />
                                  {primaryConnectionId === provider.connectionId && (
                                    <>
                                      <Status status="Primary" variant="body2" />
                                      <Box mr={2} />
                                    </>
                                  )}
                                </Flexbox>
                              }
                              isExpanded={index === 0}
                            >
                              <Flexbox
                                container
                                direction="column"
                                gutter={2}
                                style={{ width: '100%' }}
                              >
                                {displayedFields
                                  .filter((field) =>
                                    field.isVisible ? field.isVisible(provider) : true,
                                  )
                                  .map((field) => {
                                    let value;
                                    if (field.getter) {
                                      value = field.getter(provider);
                                    } else if (field.isObject) {
                                      // @ts-ignore
                                      value = provider[field.accessor]?.[field.accessKey as string];
                                    } else {
                                      // @ts-ignore
                                      value = provider[field.accessor];
                                    }

                                    const hasValue =
                                      value !== undefined && value !== null && value !== '';

                                    return (
                                      <Flexbox
                                        container
                                        item
                                        key={
                                          field.isObject
                                            ? `${field.accessor}-${field.accessKey}`
                                            : field.accessor
                                        }
                                      >
                                        <Flexbox
                                          item
                                          container
                                          style={{ width: '20%' }}
                                          justifyContent="flex-start"
                                        >
                                          <Typography variant="body2" weight="bold">
                                            {`${field.label}: `}
                                          </Typography>
                                        </Flexbox>
                                        <Flexbox
                                          item
                                          container
                                          style={{ width: '80%' }}
                                          justifyContent="flex-start"
                                        >
                                          {hasValue ? (
                                            <Typography
                                              variant="body2"
                                              color={() => 'rgb(0, 0, 0, 0.54)'}
                                            >
                                              {value?.toString()}
                                            </Typography>
                                          ) : (
                                            <Typography
                                              variant="body2"
                                              color={() => 'rgb(0, 0, 0, 0.54)'}
                                            >
                                              No {field.label}
                                            </Typography>
                                          )}
                                        </Flexbox>
                                      </Flexbox>
                                    );
                                  })}
                                <Flexbox container item key="callbackUrl">
                                  <Flexbox
                                    item
                                    container
                                    style={{ width: '20%' }}
                                    justifyContent="flex-start"
                                  >
                                    <Typography variant="body2" weight="bold">
                                      {`Callback URL: `}
                                    </Typography>
                                  </Flexbox>
                                  <Flexbox
                                    item
                                    container
                                    style={{ width: '80%' }}
                                    justifyContent="flex-start"
                                  >
                                    <Typography variant="body2" color={() => 'rgb(0, 0, 0, 0.54)'}>
                                      <ul style={{ padding: '0px 12px', margin: '0px' }}>
                                        <li>
                                          <code>
                                            {`https://${tenantAlias}.hiiretail.com/__/auth/handler`}
                                          </code>
                                        </li>
                                        <li>
                                          <code>
                                            {`https://${tenantAlias}-test.hiiretail.com/__/auth/handler`}
                                          </code>
                                        </li>
                                      </ul>
                                    </Typography>
                                  </Flexbox>
                                </Flexbox>
                                <Flexbox container item justifyContent="flex-end">
                                  {primaryConnectionId !== provider.connectionId &&
                                    provider.status !== 'Inactive' &&
                                    provider.status !== 'Inactivating' &&
                                    !!onSetPrimaryButtonClick && (
                                      <AccessControl
                                        permission="iam.tenant.update"
                                        renderIfNoAccess={
                                          <>
                                            <Tooltip
                                              title={
                                                <Typography variant="caption" color={() => 'white'}>
                                                  You do not have permissions to perform this
                                                  action.
                                                  <br />
                                                  Required permissions: {/*@ts-ignore*/}
                                                  <Typography variant="caption" weight="bold">
                                                    iam.tenant.update
                                                  </Typography>
                                                </Typography>
                                              }
                                            >
                                              <AccessDeniedButton
                                                variant="secondary"
                                                disabled
                                                data-testid={`disabled-set-primary-provider`}
                                              >
                                                {intl.formatMessage(
                                                  IdentityProvidersMessages.setPrimary,
                                                )}
                                              </AccessDeniedButton>
                                            </Tooltip>
                                            <Box mr={3} />
                                          </>
                                        }
                                      >
                                        <IdentityProviderActionButton
                                          variant="secondary"
                                          onClick={() =>
                                            onSetPrimaryButtonClick(provider.connectionId)
                                          }
                                          disabled={isSetting}
                                          data-testid={`set-primary-provider`}
                                        >
                                          {intl.formatMessage(IdentityProvidersMessages.setPrimary)}
                                        </IdentityProviderActionButton>
                                      </AccessControl>
                                    )}
                                  {primaryConnectionId === provider.connectionId &&
                                    provider.status !== 'Inactive' &&
                                    provider.status !== 'Inactivating' &&
                                    !!onUnSetPrimaryButtonClick && (
                                      <AccessControl
                                        renderIfNoAccess={
                                          <>
                                            <Tooltip
                                              title={
                                                <Typography variant="caption" color={() => 'white'}>
                                                  You do not have permissions to perform this
                                                  action.
                                                  <br />
                                                  Required permissions: {/*@ts-ignore*/}
                                                  <Typography variant="caption" weight="bold">
                                                    iam.tenant.update
                                                  </Typography>
                                                </Typography>
                                              }
                                            >
                                              <AccessDeniedButton
                                                disabled
                                                variant="secondary"
                                                data-testid={`disabled-unset-primary-provider`}
                                              >
                                                {intl.formatMessage(
                                                  IdentityProvidersMessages.unsetPrimary,
                                                )}
                                              </AccessDeniedButton>
                                            </Tooltip>
                                            <Box mr={3} />
                                          </>
                                        }
                                        permission="iam.tenant.update"
                                      >
                                        <IdentityProviderActionButton
                                          onClick={() => onUnSetPrimaryButtonClick(undefined)}
                                          disabled={isSetting}
                                          variant="secondary"
                                          data-testid={`unset-primary-provider`}
                                        >
                                          {intl.formatMessage(
                                            IdentityProvidersMessages.unsetPrimary,
                                          )}
                                        </IdentityProviderActionButton>
                                      </AccessControl>
                                    )}
                                  {(provider.status === 'Inactive' ||
                                    provider.status === 'Inactivating') &&
                                  !!onEnableButtonClick ? (
                                    <IdentityProviderActionButton
                                      variant="secondary"
                                      onClick={() => onEnableButtonClick({ id: provider.id })}
                                      disabled={isDisabling || isEnabling}
                                      data-testid={`enable-provider-${provider.id}`}
                                    >
                                      <Flexbox container alignItems="center" gutter={1}>
                                        <Flexbox item>
                                          {intl.formatMessage(CommonMessages.enable)}
                                        </Flexbox>
                                      </Flexbox>
                                    </IdentityProviderActionButton>
                                  ) : (
                                    <>
                                      {!!onDisableButtonClick && (
                                        <IdentityProviderActionButton
                                          variant="secondary"
                                          onClick={() => onDisableButtonClick({ id: provider.id })}
                                          disabled={isDisabling || isEnabling}
                                          data-testid={`disable-provider-${provider.id}`}
                                        >
                                          <Flexbox container alignItems="center" gutter={1}>
                                            <Flexbox item>
                                              {intl.formatMessage(CommonMessages.disable)}
                                            </Flexbox>
                                          </Flexbox>
                                        </IdentityProviderActionButton>
                                      )}
                                    </>
                                  )}
                                  {!!onUpdateButtonClick && (
                                    <AccessControl
                                      permission="iam.provider.update"
                                      renderIfNoAccess={
                                        <Tooltip
                                          title={
                                            <Typography variant="caption" color={() => 'white'}>
                                              You do not have permissions to perform this action.
                                              <br />
                                              Required permissions: {/*@ts-ignore*/}
                                              <Typography variant="caption" weight="bold">
                                                iam.provider.update
                                              </Typography>
                                            </Typography>
                                          }
                                        >
                                          <AccessDeniedButton
                                            variant="secondary"
                                            disabled
                                            data-testid={`update-provider-${provider.id}`}
                                          >
                                            <Flexbox container alignItems="center" gutter={1}>
                                              <Flexbox item container alignItems="center">
                                                <Icon.Edit size="sm" />
                                              </Flexbox>
                                              <Flexbox item>
                                                {intl.formatMessage(CommonMessages.edit)}
                                              </Flexbox>
                                            </Flexbox>
                                          </AccessDeniedButton>
                                        </Tooltip>
                                      }
                                    >
                                      <Button
                                        variant="secondary"
                                        onClick={() => onUpdateButtonClick(provider)}
                                        data-testid={`update-provider-${provider.id}`}
                                      >
                                        <Flexbox container alignItems="center" gutter={1}>
                                          <Flexbox item container alignItems="center">
                                            <Icon.Edit size="sm" />
                                          </Flexbox>
                                          <Flexbox item>
                                            {intl.formatMessage(CommonMessages.edit)}
                                          </Flexbox>
                                        </Flexbox>
                                      </Button>
                                    </AccessControl>
                                  )}
                                </Flexbox>
                              </Flexbox>
                            </Accordion>
                          </Flexbox>
                        ))}
                      </Flexbox>
                    </Box>
                  )}
                </>
              )}
            </>
          </AccessControl>
        )}
      </IdentityProvidersListWrapper>
    </>
  );
};
